// ============================================================================
// L'ONGLET PRIX & MARGE de la fiche article.
//
// ⚠️ LE RÉGIME D'ACHAT COMMANDE TOUT LE RESTE, et ce n'est pas une étiquette : il décide de QUI
// supporte une remise.
//   · ACHAT FERME — le prix d'achat est fixé à l'entrée en stock. Une remise en caisse ampute la marge
//     DU MAGASIN.
//   · CORNER — le coefficient est contractuel : chaque vente recalcule le prix d'achat depuis le prix
//     RÉELLEMENT ENCAISSÉ, donc le fournisseur partage la remise.
//
// ⚠️ TROIS PRIX D'ACHAT COHABITENT, ET LES CONFONDRE EST LE PIÈGE DE CET ÉCRAN (tranché avec
// l'utilisateur 2026-08-19) :
//   · LE TARIF — `article.purchase_price_excl_tax_fine`, STOCKÉ et saisi à la main. C'est ce qu'on a
//     CONVENU avec le fournisseur. Rien ne le dérive jamais, et il n'a AUCUNE valeur comptable.
//   · LE PMP — CALCULÉ depuis `purchase_entry`, c'est ce qu'on a réellement PAYÉ en moyenne. C'est LUI
//     qui fonde marge et coefficient dès qu'il existe (PCG art. 213-32 / IAS 2 : la méthode de flux
//     retenue vaut à la fois pour le stock restant et pour le coût des unités sorties). Le tarif n'est
//     qu'un repli, quand rien n'a encore été réceptionné — et l'écran DIT alors que la marge est
//     théorique.
//   · LE PA THÉORIQUE du corner — PROJECTION (PV TTC ÷ coefficient contractuel), que chaque vente
//     recalcule depuis le prix réellement encaissé.
// L'écran libellait le premier « PMP » et le légendait « moyenne pondérée des entrées » : il nommait
// une mécanique inexistante et masquait l'écart tarif ↔ réel, c'est-à-dire ce qu'on vient y chercher.
//
// ⚠️ LE COEFFICIENT A DEUX ORIGINES OPPOSÉES. En ferme il est DÉRIVÉ (PV TTC ÷ coût d'achat HT) et n'est jamais
// stocké — le stocker créerait une seconde vérité qui dériverait au premier changement de prix. En
// corner il est CONTRACTUEL : il vient de `supplier.coefficient_bp`, et c'est LUI qui commande.
//
// ⚠️ LES TUILES SE CALCULENT SUR LA SAISIE EN COURS, pas sur la base : sinon « Prix HT » contredirait
// le prix qu'on vient de taper, et on croirait s'être trompé. Le PMP fait exception — il ne dépend
// d'aucune saisie, seulement des réceptions déjà enregistrées.
// ============================================================================
import { useEffect, useState } from 'react';
import { VAT_RATES_BP, htFromTtc } from '@orgazone/core-vat';
import { achatTheoriqueCorner, indicateursPrix, ttcFromCoeff, fineEnCentimes } from './prix-derive';
import { mulDivRound, rescale } from '@orgazone/core-money';

/**
 * LA REMISE DE L'EXEMPLE CORNER — 14 %, en points de base.
 * ⚠️ NOMMÉE ET AFFICHÉE (audit 2026-08-25) : c'était un `0.86` flottant posé au milieu du rendu, sans
 * commentaire. Le lecteur ne pouvait pas savoir si l'écran montrait une remise RÉELLE du magasin ou un
 * chiffre choisi pour l'illustration — l'encart, lui, se présente comme « sur cet article ». Le taux
 * est donc écrit à l'écran, et il se lit comme ce qu'il est : un exemple.
 */
const EXAMPLE_DISCOUNT_BP = 1400;

/** Un prix d'achat théorique (échelle fine) ramené en centimes — `null` reste `null`. */
const rescaleOuNull = (fine: number | null): number | null => (fine === null ? null : rescale(fine, 4, 2));
import { formatMoney, formatMinor, parseMinor, toInputValue } from '@orgazone/money-format';
import { Field, rovingRadio, Trans } from '@orgazone/ui-web';
import { fieldLabel } from '../vocabulary';
import { api, type ArticleModelDetail, type PurchaseEntrySet } from '../api';
import { key, t, type Translatable } from '../i18n';
import { PrixDeclinaisons } from './prix-declinaisons';
import { EntreesValorisees } from './entrees-valorisees';

/**
 * Ce que la marge affichée mesure vraiment.
 * ⚠️ « THÉORIQUE » EST DIT EN TOUTES LETTRES sur le repli tarif : c'est la seule des trois qui n'a
 * aucune valeur comptable, et la seule qu'on risque de prendre pour la vraie.
 */
const BASE_MARGE: Record<'corner' | 'pmp' | 'tarif', Translatable> = {
  pmp: key('sur le PMP réel — base comptable (CUMP)'),
  tarif: key('théorique, sur le tarif — aucune réception valorisée'),
  corner: key('sur le prix d’achat théorique du corner'),
};

/** Les champs de prix qu'un modèle peut écrire — miroir de la liste fermée du serveur. */
export type ChampPrix =
  | 'salePriceInclTaxCents' | 'purchasePriceExclTaxFine' | 'vatRateBp' | 'purchaseRegime';

export interface PrixMargeProps {
  /** ⚠️ La clé de MODÈLE, pour que le bloc « prix par déclinaison » lise et écrive les siennes. */
  modelKey: string;
  sheet: ArticleModelDetail;
  edit: Partial<Record<string, string | boolean | null>>;
  setChamp: (c: ChampPrix, v: string | null, origine: string | null) => void;
  editable: boolean;
  /** Rend un avertissement de divergence — mutualisé avec l'onglet Général. */
  avertir: (n: number, valeur: string | null) => string;
  /** Prix par déclinaison en attente — tenus par la FICHE (barre commune), voir `PrixDeclinaisons`. */
  prixEdits: Record<string, string>;
  onPrixEdits: (suivant: Record<string, string>) => void;
  prixVersion: number;
}

export function PrixMarge({ modelKey, sheet, edit, setChamp, editable, avertir, prixEdits, onPrixEdits, prixVersion }: PrixMargeProps) {
  /** La saisie en cours gagne sur la base — comme partout ailleurs dans cette fiche. */
  const liveCount = (c: ChampPrix, origine: number | null, dec: number): string =>
    (c in edit ? String(edit[c] ?? '') : toInputValue(origine, dec));

  const regime = (('purchaseRegime' in edit ? String(edit.purchaseRegime ?? '') : sheet.purchaseRegime) || 'firm');
  const corner = regime === 'corner';

  /**
   * LES ENTRÉES VALORISÉES, LUES ICI ET NON DANS LEUR TABLEAU — parce que DEUX endroits s'en servent :
   * la tuile « PMP réel » en haut, et le tableau qui la justifie en bas. Deux lectures de la même route
   * finiraient par afficher deux PMP différents le temps qu'une des deux réponde.
   */
  const [entrees, setEntrees] = useState<PurchaseEntrySet | null>(null);
  const [echecEntrees, setEchecEntrees] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    api.articlePurchaseEntries(modelKey)
      .then((r) => { if (vivant) { setEntrees(r); setEchecEntrees(null); } })
      .catch((e: unknown) => { if (vivant) setEchecEntrees(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey]);

  /** Le PMP réellement constaté, en centimes — `null` tant qu'aucune entrée n'est valorisée.
   *  ⚠️ `rescale` ET NON `/ 100` : l'en-tête de `prix-derive.ts` l'interdit par écrit (« c'est là que
   *  se logent les résultats cent fois faux et plausibles »), et `Math.round` arrondit vers +∞ sur les
   *  négatifs là où `rescale` est half-up SYMÉTRIQUE. */
  const pmp = entrees?.pmpFine == null ? null : rescale(entrees.pmpFine, 4, 2);


  const pv = parseMinor(liveCount('salePriceInclTaxCents', sheet.priceMinCents, 2), { scale: 2 });
  const pa = parseMinor(liveCount('purchasePriceExclTaxFine', sheet.costMinFine, 4), { scale: 4 });
  const tvaBp = Number('vatRateBp' in edit ? edit.vatRateBp : sheet.vatRateBp) || 0;

  const ttc = pv.state === 'ok' ? pv.minor : null;
  /** Le TARIF convenu (échelle fine), tel qu'il est stocké sur l'article — jamais le PMP. */
  const paSaisi = pa.state === 'ok' ? pa.minor : null;

  /**
   * L'ÉCART ENTRE LE TARIF CONVENU ET CE QU'ON A PAYÉ — la seule raison d'afficher les deux côte à côte.
   * ⚠️ Sans lui, on met deux nombres proches l'un sous l'autre et c'est au lecteur de faire la
   * soustraction : le seul chiffre qui l'intéresse resterait le seul qu'on ne montre pas.
   * ⚠️ Absent en CORNER : le tarif n'y commande rien (c'est le coefficient), et le comparer à un champ
   * que l'écran ne montre même plus n'aurait aucun sens.
   */
  const wacGap = ((): string | undefined => {
    if (corner || pmp === null || paSaisi === null) return undefined;
    const tarif = rescale(paSaisi, 4, 2);
    const d = pmp - tarif;
    if (d === 0) return t('conforme au tarif');
    return t('{ecart} vs tarif', { ecart: `${d > 0 ? '+' : '−'}${formatMoney(Math.abs(d), { thousandsSep: ' ' })}` });
  })();
  // ⚠️ La ventilation vient de `core-vat` : la règle TVA est FIGÉE (§30.3) et doit être appliquée à
  // l'identique en UI, backend et export comptable. La refaire ici en ferait une seconde.
  const ht = ttc === null ? null : htFromTtc(ttc, tvaBp);
  /**
   * LE COEFFICIENT CONTRACTUEL, en points de base — la donnée qui COMMANDE le régime corner.
   * ⚠️ Il vient du fournisseur, jamais de l'article : le poser par article en ferait N copies d'une
   * même clause, dont la première renégociation en laisserait la moitié périmée.
   */
  const contractCoeffBp = sheet.supplierCoefficientBp;

  /**
   * EN CORNER, LE PRIX D'ACHAT SE DÉDUIT ; EN FERME, IL EST MESURÉ. C'est toute la différence entre les
   * deux régimes, et elle doit traverser l'écran entier.
   *
   * ⚠️ SANS COEFFICIENT AU CONTRAT, IL N'Y A PAS DE PRIX D'ACHAT EN CORNER — et donc pas de marge non
   * plus. Reprendre le PMP stocké à la place serait le chiffre du régime FERME affiché sous une fiche
   * corner : plausible, faux, et invisible. On rend « N/C » et la note dit où saisir le coefficient (§23.0).
   * ⚠️ UN SEUL COEFFICIENT, DU PRIX D'ACHAT HT AU PRIX DE VENTE TTC (décision utilisateur, `prix-derive.ts`) : le
   * coefficient fournisseur se lit comme celui de l'article, donc PA HT = PV TTC ÷ coef — une lecture sur le HT en
   * donnerait une seconde définition, qui différerait de la TVA.
   */
  const paTheoriqueFine = corner ? achatTheoriqueCorner(ttc, contractCoeffBp) : null;

  /**
   * LE COÛT SUR LEQUEL MARGE ET COEFFICIENT SE CALCULENT (échelle fine) — et il n'est PAS le tarif.
   *
   * ⚠️ SUR LE PMP DÈS QU'IL EXISTE, ET C'EST LA RÈGLE COMPTABLE. PCG art. 213-32 et IAS 2 : la méthode
   * de flux retenue — CUMP (notre PMP) ou FIFO, le LIFO étant interdit — détermine **à la fois** la
   * valeur du stock restant **et** le coût des unités sorties. La marge commerciale (ventes de
   * marchandises − coût d'achat des marchandises VENDUES) se calcule donc sur le PMP.
   * ⚠️ LE TARIF N'EST QU'UN REPLI, et l'écran le DIT. Il n'a aucune valeur comptable : c'est une
   * référence de négociation. La marge qu'il produit est THÉORIQUE — utile pour fixer un prix, et seule
   * disponible sur un article jamais réceptionné, mais ce n'est pas « la marge ».
   * ⚠️ EN CORNER, NI L'UN NI L'AUTRE : le coût se recalcule à chaque vente depuis le coefficient
   * contractuel, et c'est cette projection qui fait foi.
   */
  const purchase = corner ? paTheoriqueFine : (entrees?.pmpFine ?? paSaisi);

  /** Sur quoi la marge affichée repose — jamais implicite : deux articles voisins peuvent différer. */
  const baseMarge: 'corner' | 'pmp' | 'tarif' = corner ? 'corner' : (entrees?.pmpFine != null ? 'pmp' : 'tarif');

  /**
   * LE COEFFICIENT AFFICHÉ, en centièmes (250 = 2,50) — de deux ORIGINES opposées selon le régime.
   *
   * ⚠️ EN CORNER IL N'EST PAS DÉRIVÉ : il EST la donnée d'entrée. Le recalculer depuis un prix d'achat
   * qu'il vient lui-même de produire serait circulaire — et surtout, ça masquerait son absence en
   * affichant un nombre parfaitement crédible.
   * ⚠️ EN FERME, DÉLÉGUÉ À `ratioScaled` DE core-money : le cœur le fait en BigInt, donc exact quelle
   * que soit l'échelle, et avec le MÊME arrondi que la marge d'à côté — un rapport calculé à la main
   * finit par arrondir autrement que sa voisine. Les deux montants sont ramenés à la même échelle avant
   * division (prix de vente TTC en centimes, achat en fine ×100) : les diviser tels quels donnerait un
   * coefficient cent fois trop petit.
   * ⚠️ `null` et PAS zéro quand rien ne le fonde : « pas de coefficient » et « coefficient nul » ne
   * disent pas la même chose, et zéro se lirait comme une marge totale.
   */
  // ⚠️ UNE SEULE ARITHMÉTIQUE POUR LES DEUX ÉCRANS : la fiche et l'assistant Gamme montrent les mêmes
  // indicateurs sur les mêmes montants. Recalculés de chaque côté, ils finiraient par diverger, et un
  // même article afficherait deux coefficients selon l'écran qui le montre (Forme A).
  const { margeBp: marge, margeCents: margeEur, coeffCents: coeff } = indicateursPrix(
    ttc, purchase, tvaBp, corner ? contractCoeffBp : null,
  );

  const prixDivergent = sheet.priceMaxCents !== null && sheet.priceMaxCents !== sheet.priceMinCents;

  /**
   * LE COEFFICIENT SAISI — état LOCAL, jamais envoyé au serveur.
   *
   * ⚠️ IL N'EST PAS STOCKÉ (décision utilisateur) : c'est un outil de SAISIE, pas une donnée. On tape un
   * coefficient, il en déduit un prix, et c'est LE PRIX qui part. Le retenir en base créerait une
   * seconde vérité qui dériverait au premier changement de tarif.
   * ⚠️ Vide = « pas de saisie en cours » : la tuile affiche alors le coefficient DÉRIVÉ des montants.
   */
  const [coeffInput, setCoeffInput] = useState('');


  /**
   * SAISIR UN COEFFICIENT RECALCULE LE PRIX DE VENTE — et inversement (relevé maquette l.16833-16842 :
   * « Saisir un coef recalcule le PV depuis le PA ; saisir un PV recalcule le coef »).
   *
   * ⚠️ LE COEFFICIENT VA DU PRIX D'ACHAT HT AU PRIX DE VENTE TTC (décision utilisateur) : PV TTC = PA HT × coef,
   * sans étape HT — la règle du coefficient fournisseur, de la page Prix et du Reporting. La page Prix de la
   * maquette écrit l'inverse, consigné dans ses conventions : deux définitions afficheraient deux coefficients pour
   * un même article selon l'écran qui le montre.
   *
   * ⚠️ EN CORNER, SAISIR UN COEFFICIENT NE TOUCHE À RIEN : le coefficient y est CONTRACTUEL, c'est lui
   * qui commande le prix d'achat à chaque vente. Le laisser modifier le prix de vente inverserait le
   * sens de la relation.
   */
  const applyCoeff = (texte: string) => {
    setCoeffInput(texte);
    if (corner) return;
    const c = parseMinor(texte, { scale: 2 });
    if (c.state !== 'ok') return;
    // ⚠️ LE CALCUL VIT DANS `ttcFromCoeff`, partagé avec l'assistant Gamme : refait ici, l'arrondi ne tomberait pas
    //    au même endroit, et le même coefficient donnerait un prix différent selon l'écran — or c'est ce prix-là qui
    //    part en base et descend aux caisses.
    const ttcVise = ttcFromCoeff(purchase, c.minor);
    if (ttcVise === null) return;
    setChamp('salePriceInclTaxCents', toInputValue(ttcVise, 2), toInputValue(sheet.priceMinCents, 2));
  };

  /**
   * L'EXEMPLE CHIFFRÉ DU CORNER — ce que la remise fait au prix d'achat.
   *
   * ⚠️ LE MÊME CALCUL QUE LA TUILE « Prix d'achat théorique » (`achatTheoriqueCorner`, PV TTC ÷ coef) : deux calculs
   * afficheraient deux prix d'achat théoriques pour le même article, à 150 px l'un de l'autre. La remise passe par
   * `mulDivRound`, jamais par un flottant.
   * ⚠️ SANS COEFFICIENT CONTRACTUEL, ON REND `null` : la tuile avoue « N/C » dans ce cas, et un encart
   * qui continuerait d'imprimer un montant crédible à côté d'elle serait exactement l'écran qui ment.
   */
  const exemple = ((): { discountInclTax: number; paPlein: number | null; discountedPurchasePrice: number | null } => {
    const t = ttc ?? 0;
    const discountInclTax = mulDivRound(t, 10_000 - EXAMPLE_DISCOUNT_BP, 10_000);
    const bp = contractCoeffBp;
    return {
      discountInclTax,
      paPlein: bp === null ? null : rescaleOuNull(achatTheoriqueCorner(ttc, bp)),
      discountedPurchasePrice: bp === null ? null : rescaleOuNull(achatTheoriqueCorner(discountInclTax, bp)),
    };
  })();

  if (!editable) return <ReadOnlyView sheet={sheet} ht={ht} coeff={coeff} marge={marge} />;

  return (
    // ⚠️ `minmax(0, 1fr)` ET PAS LE `1fr` IMPLICITE — sans lui l'onglet déborde de la modale. Un enfant
    // de grille a `min-width: auto`, c'est-à-dire la largeur de son CONTENU : il ne descend donc jamais
    // en dessous, et la matrice des prix par déclinaison (118 + n×74 + 150 px) élargit la colonne, donc
    // la modale, MALGRÉ son propre ascenseur interne. Résultat mesuré : un ascenseur horizontal sur
    // toute la fiche dès qu'un modèle a beaucoup de tailles. La maquette n'a pas le problème parce que
    // son panneau est en flux de blocs ; c'est la grille qui l'a introduit.
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 20 }}>
      <Bloc heading={fieldLabel('purchaseRegime')}>
        <div role="radiogroup" aria-label={fieldLabel('purchaseRegime')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ChoiceCard
            index={0} pick={(k) => setChamp('purchaseRegime', k === 1 ? 'corner' : 'firm', sheet.purchaseRegime)}
            checked={!corner}
            heading={t('Achat ferme')}
            text={t('Le prix d’achat est fixé à l’entrée en stock — la remise en caisse ampute votre marge.')}
            onChoose={() => setChamp('purchaseRegime', 'firm', sheet.purchaseRegime)}
          />
          <ChoiceCard
            index={1} pick={(k) => setChamp('purchaseRegime', k === 1 ? 'corner' : 'firm', sheet.purchaseRegime)}
            checked={corner}
            heading={t('Corner — PA calculé à la vente')}
            text={t('Le coefficient est contractuel : chaque vente calcule son prix d’achat depuis le prix réellement encaissé — le fournisseur partage la remise.')}
            onChoose={() => setChamp('purchaseRegime', 'corner', sheet.purchaseRegime)}
          />
        </div>
        {sheet.purchaseRegimeMixed && <Alerte>{avertir(sheet.variantCount, sheet.purchaseRegime)}</Alerte>}
      </Bloc>

      <Bloc heading={t('Tarif commun')}>
        <Grid>
          <div>
            {/* ⚠️ EN CORNER, LE PRIX D'ACHAT N'EST PLUS UNE DONNÉE : c'est une PROJECTION (PV ÷ coef),
                recalculée à chaque vente depuis le prix réellement encaissé. Le laisser saisissable
                — ce qu'il était — invitait à taper un nombre que rien n'aurait conservé : la première
                vente l'aurait écrasé, sans un mot. La maquette (l.3676) en fait un pavé violet en
                pointillé, non modifiable ; le pointillé DIT que la valeur est dérivée, pas figée. */}
            {corner ? (
              <>
                <EtiquetteChamp>{t('Prix d’achat théorique')}</EtiquetteChamp>
                <PaveContractuel valeur={paTheoriqueFine === null ? 'N/C' : formatMoney(fineEnCentimes(paTheoriqueFine), { thousandsSep: ' ' })} />
                <Note>{t('Théorique — PV catalogue ÷ coef. Le PA réel se fige à chaque vente.')}</Note>
              </>
            ) : (
              <>
                {/* ⚠️ CE CHAMP N'EST PAS LE PMP — divergence avec la maquette, TRANCHÉE PAR L'UTILISATEUR
                    (2026-08-19). Il est STOCKÉ sur l'article et n'est écrit que par une saisie ou une
                    action de masse : rien ne le dérive jamais des réceptions. La maquette le légendait
                    « Prix moyen pondéré des entrées », ce qui nommait une mécanique inexistante — un
                    libellé qui ment sur ce qu'il montre (§23.0). Les deux valeurs COEXISTENT désormais :
                    ce champ porte le tarif convenu, la tuile « PMP réel » porte ce qu'on a payé. */}
                <Field
                  label={fieldLabel('purchasePriceExclTax')}
                  value={liveCount('purchasePriceExclTaxFine', sheet.costMinFine, 4)}
                  onChange={(e) => setChamp('purchasePriceExclTaxFine', e.target.value || null, toInputValue(sheet.costMinFine, 4))}
                  pending={'purchasePriceExclTaxFine' in edit}
                  inputMode="decimal"
                  placeholder="N/C"
                  error={pa.state === 'rejected' ? pa.reason : undefined}
                  style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                />
                <Note>{t('Tarif convenu avec le fournisseur. La marge, elle, se calcule sur le PMP réel dès qu’une réception est valorisée.')}</Note>
                {/* Le PMP CONSTATÉ vit dans les tuiles calculées, avec les autres valeurs dérivées. */}
              </>
            )}
          </div>

          <div>
            {/* ⚠️ EN CORNER, LE COEFFICIENT EST CONTRACTUEL — il vient du fournisseur, pas de l'écran.
                On le MONTRE au lieu de l'offrir à la saisie : un champ modifiable y laisserait croire
                qu'on peut renégocier un contrat depuis une fiche article. */}
            {corner ? (
              <>
                <EtiquetteChamp>{t('Coefficient')}</EtiquetteChamp>
                {/* Le CADENAS, relevé l.3701 : c'est lui qui dit « ceci vient d'ailleurs », avant même
                    qu'on lise la note. */}
                <PaveContractuel
                  cadenas
                  valeur={contractCoeffBp === null ? 'N/C' : formatMinor(contractCoeffBp, { scale: 4, displayScale: 2 })}
                />
                {/* ⚠️ QUAND IL MANQUE, ON DIT OÙ LE POSER — et l'endroit doit EXISTER. Cette note
                    envoyait vers une « fiche fournisseur » qui n'a jamais existé : un libellé qui
                    nomme un mécanisme absent fait chercher, puis renoncer. Le réglage vit dans
                    Référentiels → Fournisseurs & marques, et la MARQUE y surcharge le fournisseur. */}
                <Note>{contractCoeffBp === null
                  ? (sheet.supplier
                    ? t('Aucun coefficient au contrat de {fournisseur} — à poser dans Référentiels › Fournisseurs & marques.', { fournisseur: sheet.supplier })
                    : t('Aucun coefficient au contrat — à poser dans Référentiels › Fournisseurs & marques.'))
                  : (sheet.brand
                    ? t('Contractuel — de {marque} ou de {fournisseur}, posé aux Référentiels.', { marque: sheet.brand, fournisseur: sheet.supplier ?? t('son fournisseur') })
                    : t('Contractuel — {fournisseur}, posé aux Référentiels.', { fournisseur: sheet.supplier ?? t('son fournisseur') }))}</Note>
              </>
            ) : (
              <>
                <Field
                  label={t('Coefficient')}
                  value={coeffInput !== '' ? coeffInput : (coeff === null ? '' : formatMinor(coeff, { scale: 2, displayScale: 2 }))}
                  onChange={(e) => applyCoeff(e.target.value)}
                  inputMode="decimal"
                  placeholder="—"
                  style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                />
                <Note>{t('Saisir un coef recalcule le prix de vente depuis le prix d’achat ; saisir un prix recalcule le coef.')}</Note>
              </>
            )}
          </div>

          <div>
            <span style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 }}>
              {fieldLabel('vatRate')}
            </span>
            {/* ⚠️ LISTE FERMÉE, tirée de `core-vat` : la règle TVA est figée (§30.3). Un taux libre
                produirait des ventilations que le fiscal refuserait. */}
            <select
              className="oz-field"
              value={String(tvaBp)}
              aria-label={fieldLabel('vatRate')}
              onChange={(e) => setChamp('vatRateBp', e.target.value, String(sheet.vatRateBp ?? 0))}
              style={{ width: '100%', height: 40, padding: '0 12px', fontSize: 'var(--t-title)' }}
            >
              {(Object.values(VAT_RATES_BP) as number[]).map((bp) => (
                <option key={bp} value={String(bp)}>{toInputValue(bp, 2)} %</option>
              ))}
            </select>
            {sheet.vatMixed && <Alerte>{avertir(sheet.variantCount, String(sheet.vatRateBp ?? ''))}</Alerte>}
          </div>

          {/* ⚠️ LE PRIX DE VENTE FERME LA RANGÉE — ordre de la maquette (l.3700), pas le mien : on
              descend du coût vers le prix, en passant par ce qui les relie (coefficient, TVA). */}
          <div>
            <Field
              label={fieldLabel('salePriceInclTax')}
              value={liveCount('salePriceInclTaxCents', sheet.priceMinCents, 2)}
              onChange={(e) => setChamp('salePriceInclTaxCents', e.target.value || null, toInputValue(sheet.priceMinCents, 2))}
              pending={'salePriceInclTaxCents' in edit}
              inputMode="decimal"
              placeholder="N/C"
              error={pv.state === 'rejected' ? pv.reason : undefined}
              style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
            />
            {/* ⚠️ UN TARIF COMMUN APLATIT LES PRIX PAR DÉCLINAISON. Sur de l'argent, ça se dit AVANT :
                la fourchette est la seule trace qu'une variation existait. */}
            {prixDivergent && (
              <Alerte>
                {t('Les déclinaisons vont de {min} à {max} : enregistrer posera le même prix sur toutes.', { min: formatMoney(sheet.priceMinCents ?? 0, { thousandsSep: ' ' }), max: formatMoney(sheet.priceMaxCents ?? 0, { thousandsSep: ' ' }) })}
              </Alerte>
            )}
          </div>
        </Grid>

        {/* ⚠️ LES TUILES VIVENT DANS LE MÊME BLOC QUE LES CHAMPS (maquette l.3691) — elles n'ont PAS de
            titre « Calculé ». Un intertitre en ferait une section autonome, alors qu'elles ne sont que
            la lecture immédiate de ce qu'on vient de saisir juste au-dessus. */}
        <TilesGrid>
          <Tile label={t('Prix HT')} valeur={ht === null ? null : formatMoney(ht, { thousandsSep: ' ' })} />
          <Tile label={t('Dont TVA')} valeur={ht === null || ttc === null ? null : formatMoney(ttc - ht, { thousandsSep: ' ' })} />
          {/* ⚠️ LE PMP EST CE QU'ON A PAYÉ, le champ ci-dessus est ce qu'on avait CONVENU. Les confondre
              — ce que faisait l'écran — masque exactement ce qu'on vient chercher ici : l'écart entre le
              tarif négocié et la réalité des réceptions. Il est ici parce qu'il est CALCULÉ ; le tableau
              du bas le justifie ligne à ligne. */}
          <Tile
            label={t('PMP réel')}
            valeur={pmp === null ? null : formatMoney(pmp, { thousandsSep: ' ' })}
            sous={wacGap}
            note={pmp === null
              ? (echecEntrees === null ? t('aucune entrée valorisée — voir le tableau ci-dessous') : t('entrées illisibles'))
              : t('moyenne pondérée des réceptions')}
          />
          {/* ⚠️ LA BASE EST AFFICHÉE, TOUJOURS. Deux articles côte à côte peuvent avoir des marges
              calculées différemment — l'un sur son PMP, l'autre sur son tarif faute de réception. Sans
              cette mention, on comparerait deux nombres qui ne mesurent pas la même chose. */}
          <Tile
            teal
            label={t('Marge')}
            valeur={marge === null ? null : `${formatMinor(marge, { scale: 2, displayScale: 1 })} %`}
            sous={margeEur === null ? undefined : formatMoney(margeEur, { thousandsSep: ' ' })}
            note={marge === null ? t('sans coût d’achat, la marge ne se calcule pas') : t(BASE_MARGE[baseMarge])}
          />
          <Tile
            label={t('Coefficient')}
            valeur={coeff === null ? null : formatMinor(coeff, { scale: 2, displayScale: 2 })}
            // Il repose sur le MÊME coût que la marge — sinon les deux tuiles se contrediraient, et on
            // pourrait déduire de l'une un chiffre que l'autre dément.
            note={coeff === null ? t('sans coût d’achat, il ne se calcule pas') : t(BASE_MARGE[baseMarge])}
          />
        </TilesGrid>
      </Bloc>

      {/* ⚠️ D'OÙ VIENT LE COEFFICIENT, EN CORNER : de la MARQUE si elle en porte un, de son
          FOURNISSEUR sinon (`COALESCE(brand, supplier)` côté lecture). Il se pose dans Référentiels ›
          Fournisseurs & marques, et nulle part ailleurs : saisi par article, il en existerait N
          copies dont la première renégociation laisserait la moitié périmée. */}
      {/* ⚠️ « EXEMPLE SUR CET ARTICLE » — relevé maquette l.3720. Il n'est là QU'EN CORNER, et c'est
          tout son intérêt : le régime corner ne se comprend pas par sa définition, il se comprend en
          voyant ce qu'une REMISE fait au prix d'achat. Sur le prix RÉEL de cet article, pas sur un cas
          d'école — « 100 € » ne prouve rien, « 69,99 € » montre l'arrondi qui tombera vraiment. */}
      {corner && ttc !== null && coeff !== null && (
        <p style={{
          margin: 0, padding: '13px 15px', borderRadius: 'var(--r-sm)',
          // ⚠️ VIOLET, PAS GRIS (maquette l.3722) : le violet est la couleur du CORNER dans tout cet
          // onglet — le prix d'achat théorique, le coefficient contractuel et cet exemple en sont les
          // trois faces. En gris, l'encart se lisait comme une note de bas de page parmi d'autres.
          background: 'var(--violet-bg)', border: '1px solid var(--violet-fg)',
          fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.65,
        }}>
          <Trans
            k="<1>Exemple sur cet article :</1> affiché {affiche}, remisé {remise} en caisse (remise d’exemple {taux} %), coefficient {coeff} → le prix d’achat de CETTE vente devient <2>{paRemise}</2> au lieu de {paPlein} à plein tarif — le fournisseur porte sa part de la remise. La marge affichée en statistiques utilise le PA réel de chaque vente — jamais le théorique."
            vars={{
              affiche: formatMoney(ttc, { thousandsSep: ' ' }),
              remise: formatMoney(exemple.discountInclTax, { thousandsSep: ' ' }),
              taux: formatMinor(EXAMPLE_DISCOUNT_BP, { scale: 2, displayScale: 0 }),
              coeff: formatMinor(coeff, { scale: 2, displayScale: 2 }),
              paRemise: exemple.discountedPurchasePrice === null ? 'N/C' : formatMoney(exemple.discountedPurchasePrice, { thousandsSep: ' ' }),
              paPlein: exemple.paPlein === null ? 'N/C' : formatMoney(exemple.paPlein, { thousandsSep: ' ' }),
            }}
            parts={[
              (x) => <strong style={{ color: 'var(--violet-fg)' }}>{x}</strong>,
              (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>,
            ]}
          />
        </p>
      )}

      {/* ⚠️ L'ENCART NE SE MONTRE QUE S'IL N'Y A RIEN À LIRE (correctif d'audit 2026-08-25). Il
          s'affichait sur TOUT article corner et affirmait qu'« aucun écran ni import n'écrit le
          coefficient contractuel » — alors que Référentiels › Fournisseurs & marques l'écrit, que la
          lecture le sert en `COALESCE(marque, fournisseur)`, et que le pavé cadenassé 140 lignes plus
          haut annonce déjà « Contractuel — posé aux Référentiels ». L'écran se contredisait donc à
          l'intérieur d'une même page, et c'était le message ALARMANT qui gagnait : un gérant croyant
          son 2,50 « déduit des prix saisis » retape un prix de vente pour corriger un coefficient qui
          était juste. Il ne subsiste que pour son cas VRAI — le coefficient manque réellement.
          ⚠️ ET IL NE PARLE PLUS D'UN COEFFICIENT « DÉDUIT DES PRIX SAISIS » (revue 2026-08-25) : dans ce
          cas précis l'écran n'en affiche AUCUN — sans clause, `achatTheoriqueCorner` rend `null`, donc
          la tuile Coefficient rend « sans coût d'achat, il ne se calcule pas ». La phrase mettait en
          garde contre un nombre absent, ce qui envoyait chercher une erreur là où il n'y a qu'un
          référentiel à remplir. */}
      {corner && contractCoeffBp === null && (
        <p style={{
          margin: 0, padding: '11px 14px', borderRadius: 'var(--r-sm)',
          background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          fontSize: 'var(--t-meta)', color: 'var(--ink-800)', lineHeight: 1.55,
        }}>
          <Trans k='Aucun <1>coefficient contractuel</1> n’est posé sur la marque ni sur le fournisseur de cet article. En régime corner le coût vient de cette clause et de nulle part ailleurs : sans elle, le <2>prix d’achat théorique</2> et la <3>marge</3> restent incalculables — c’est ce que « N/C » signifie ci-dessus. Elle se pose dans <4>Référentiels › Fournisseurs & marques</4>.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></p>
      )}

      <PrixDeclinaisons modelKey={modelKey} prixCommunCents={sheet.priceMinCents} editable={editable}
        edits={prixEdits} onEdits={onPrixEdits} version={prixVersion} />

      {/* ⚠️ EN DERNIER, ET C’EST L’ORDRE DE LA MAQUETTE (l.3785) : le tableau JUSTIFIE le prix
          d’achat affiché tout en haut. On le lit quand on doute du chiffre, pas avant. */}
      <EntreesValorisees jeu={entrees} failure={echecEntrees} />

      {/* ⚠️ LE PIED « AUCUN FLUX DE RÉCEPTION » A ÉTÉ RETIRÉ (audit 2026-08-25). Il affirmait, trois
          lignes sous le tableau qui les liste, que les entrées valorisées « ne sont pas encore ici ».
          Le flux existe depuis : bons de livraison, rétrocessions et retours fournisseur écrivent tous
          `purchase_entry`. `EntreesValorisees` porte déjà son propre état vide ET son propre canal
          d'échec — c'est LUI qui dit la vérité quand il n'y a rien, et il la dit sans se contredire.
          C'est la page qui justifie le PMP : deux affirmations opposées à 40 px l'une de l'autre y
          coûtent la confiance dans le chiffre du haut. */}
    </div>
  );
}

/** L'onglet en LECTURE SEULE — les mêmes valeurs, sans aucun contrôle. */
function ReadOnlyView({ sheet, ht, coeff, marge }: {
  sheet: ArticleModelDetail; ht: number | null; coeff: number | null; marge: number | null;
}) {
  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <Bloc heading={t('Tarif du modèle')}>
        <Grid>
          <Tile label={fieldLabel('purchaseRegime')} valeur={sheet.purchaseRegime === 'corner' ? t('Corner') : t('Achat ferme')} />
          <Tile
            label={fieldLabel('salePriceInclTax')}
            valeur={sheet.priceMinCents === null ? null : formatMoney(sheet.priceMinCents, { thousandsSep: ' ' })}
          />
          <Tile
            label={fieldLabel('purchasePriceExclTax')}
            valeur={sheet.costMinFine === null ? null : formatMoney(sheet.costMinFine, { scale: 4, displayScale: 2, thousandsSep: ' ' })}
          />
          <Tile label={fieldLabel('vatRate')} valeur={`${formatMinor(sheet.vatRateBp ?? 0, { scale: 2, displayScale: 2 })} %`} />
          <Tile label={t('Prix HT')} valeur={ht === null ? null : formatMoney(ht, { thousandsSep: ' ' })} />
          <Tile label={t('Marge')} valeur={marge === null ? null : `${formatMinor(marge, { scale: 2, displayScale: 1 })} %`} />
          <Tile label={t('Coefficient')} valeur={coeff === null ? null : formatMinor(coeff, { scale: 2, displayScale: 2 })} />
        </Grid>
      </Bloc>
      <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.6 }}>
        
        {t('Lecture seule : votre compte n’a pas le droit d’écrire dans ce magasin.')}
      </p>
    </div>
  );
}

// ── PRIMITIVES DE L'ONGLET ─────────────────────────────────────────────────────────────────────

function Bloc({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    // ⚠️ `minWidth: 0` — caper la PISTE de grille ne suffit pas : l'ENFANT garde `min-width: auto`,
    // c'est-à-dire la largeur de son contenu, et déborde de sa colonne. Il faut lever les deux.
    <section style={{ minWidth: 0 }}>
      <h3 style={{
        margin: '0 0 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)',
        fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-500)',
      }}>{heading}</h3>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
      gap: 13, alignItems: 'start',
    }}>{children}</div>
  );
}

/**
 * UNE CARTE DE CHOIX EXCLUSIF — un rang dans un groupe radio : le régime d'achat ici, la nature de l'article dans la fiche.
 * ⚠️ La conséquence est ÉCRITE sur la carte, pas cachée dans une infobulle : choisir un régime décide de qui supporte
 *    les remises, et personne ne survole avant de cliquer.
 * ⚠️ PARTAGÉE plutôt que recopiée : les flèches du groupe (`rovingRadio`) doivent connaître le NOMBRE d'options, et
 *    une copie à deux options posée sur un groupe de trois laisserait la troisième hors d'atteinte au clavier.
 */
export function ChoiceCard({ index, count = 2, pick, checked, anyChecked = true, heading, text, onChoose }: {
  /** Son rang dans le groupe, et le choix par rang — les flèches du groupe passent par lui. */
  index: number; count?: number; pick: (index: number) => void;
  /** `false` quand aucune carte du groupe n'est cochée — la première reste alors atteignable au clavier. */
  checked: boolean; anyChecked?: boolean; heading: string; text: string; onChoose: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      {...rovingRadio({ index, count, checked, anyChecked, onPick: pick })}
      onClick={onChoose}
      style={{
        flex: 1, minWidth: 230, display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '11px 13px', textAlign: 'left', cursor: 'pointer',
        borderRadius: 'var(--r-sm)',
        border: `1px solid var(--${checked ? 'teal-400' : 'ink-200'})`,
        background: checked ? 'var(--teal-100)' : 'var(--paper)',
      }}
    >
      <span aria-hidden style={{
        width: 16, height: 16, borderRadius: 'var(--r-pill)', flexShrink: 0, marginTop: 1, boxSizing: 'border-box',
        border: `2px solid var(--${checked ? 'teal-600' : 'ink-300'})`,
        background: checked ? 'var(--teal-600)' : 'transparent',
        boxShadow: checked ? 'inset 0 0 0 3px var(--paper)' : undefined,
      }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{heading}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.45, marginTop: 2 }}>
          {text}
        </span>
      </span>
    </button>
  );
}

/**
 * Une valeur CALCULÉE — carte bordée, relevée l.3696-3735 de la maquette.
 * ⚠️ LA MARGE EST LA SEULE EN TEAL, et ce n'est pas décoratif : c'est le chiffre qu'on vient chercher
 * dans cet onglet. Les trois autres l'expliquent, elle seule se lit d'un coup d'œil depuis l'autre bout
 * de l'écran.
 * ⚠️ L'absence se dit « N/C » et porte SA raison : un tiret nu laisserait croire à un défaut
 * d'affichage, alors que c'est une donnée qui manque en amont.
 */
function Tile({ label, valeur, sous, note, teal }: {
  label: string; valeur: string | null; sous?: string; note?: string; teal?: boolean;
}) {
  const encre = teal ? 'var(--teal-800)' : 'var(--ink-900)';
  return (
    <div style={{
      minWidth: 0, padding: 15, borderRadius: 'var(--r-sm)',
      border: `1px solid var(--${teal ? 'teal-300' : 'ink-200'})`,
      background: teal ? 'var(--teal-100)' : undefined,
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
        textTransform: 'uppercase', color: teal ? 'var(--teal-800)' : 'var(--ink-500)',
      }}>{label}</div>
      <div style={{
        fontSize: 'var(--t-h2)', fontWeight: 800, letterSpacing: '-0.025em', marginTop: 5, whiteSpace: 'nowrap',
        color: valeur === null ? 'var(--ink-400)' : encre, fontVariantNumeric: 'tabular-nums',
      }}>{valeur ?? 'N/C'}</div>
      {sous && (
        <div style={{
          fontSize: '11.5px', color: encre, opacity: 0.8, marginTop: 2, fontVariantNumeric: 'tabular-nums',
        }}>{sous}</div>
      )}
      {note && (
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 3, lineHeight: 1.45 }}>{note}</div>
      )}
    </div>
  );
}

/**
 * La rangée des valeurs calculées — grille PROPRE aux tuiles (l.3691).
 * ⚠️ Ses crans sont plus serrés que ceux des champs (128 px contre 170, gouttière 11 contre 13) :
 * une tuile ne porte qu'un nombre, un champ porte une saisie et son aide.
 */
function TilesGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))',
      gap: 11, marginTop: 18,
    }}>{children}</div>
  );
}

/** L'étiquette d'un champ, quand la valeur n'est pas un `Field` — même graisse, même écart. */
function EtiquetteChamp({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'block', fontSize: 'var(--t-note)', fontWeight: 500,
      color: 'var(--ink-500)', marginBottom: 5,
    }}>{children}</span>
  );
}

/**
 * UNE VALEUR QUI VIENT DU CONTRAT, PAS DE L'ÉCRAN — pavé violet en pointillé (maquette l.3676/3689).
 *
 * ⚠️ CE N'EST PAS UN CHAMP GRISÉ, ET LA NUANCE COMPTE. Un `<input disabled>` dit « vous n'avez pas le
 * droit » ; ce pavé dit « il n'y a rien à saisir ici ». La valeur est DÉRIVÉE (prix d'achat) ou
 * CONTRACTUELLE (coefficient) — dans les deux cas, la taper n'aurait aucun effet, parce que la
 * prochaine vente la recalculerait.
 * ⚠️ LE POINTILLÉ EST LA MARQUE DU DÉRIVÉ, le violet celle du corner : les deux ensemble distinguent
 * ces pavés au premier coup d'œil des champs pleins d'à côté.
 */
function PaveContractuel({ valeur, cadenas }: { valeur: string; cadenas?: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      // Le cadenas se pose à GAUCHE et la valeur à droite ; sans lui, la valeur seule reste à droite.
      justifyContent: cadenas ? 'space-between' : 'flex-end',
      gap: 8, height: 40, padding: '0 12px', boxSizing: 'border-box',
      border: '1px dashed var(--violet-fg)', borderRadius: 'var(--r-ctrl)',
      background: 'var(--violet-bg)', color: 'var(--violet-fg)',
      fontSize: 'var(--t-title)', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
    }}>
      {cadenas && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
      )}
      {valeur}
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '5px 0 0', fontSize: 'var(--t-note)', color: 'var(--ink-400)', lineHeight: 1.5 }}>{children}</p>;
}

function Alerte({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '5px 0 0', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>{children}</p>;
}
